<template>
  <div
      ref="toastRef"
      :style="selectedStyle"
      class="notification-group">
    <transition-group
        :css="false"
        @enter="onToastEnter"
        @leave="onToastLeave"
        @after-leave="onToastAfterLeave">

      <!--      list of active toasts -->
      <div
          v-for="toast in activeToasts"
          :key="toast.id"
          :data-id="toast.id"
          class="toast-top-layer"
          @mouseenter="onMouseEnterOverToast(toast)"
          @mouseleave="onMouseLeaveOverToast(toast)"
      >
        <!--  slot so we can define our own design of toast. -->
        <slot
            :class="[defaultTemplateClassName, toast.type]"
            :close="() => onCloseToastRequest(toast)"
            :toast="toast"
            name="toast"
        >
          <!--        Default slot template -->
          <div v-show="toast.show" :class="[toast.type]" class="custom-template" @click="onCloseToastRequest(toast)">
            <!--          <div class="custom-template-icon" v-show="">-->
            <!--            <i class="icon ion-android-checkmark-circle"></i>-->
            <!--          </div>-->
            <div class="custom-template-content">
              <div class="custom-template-title" v-html="toast.title"></div>
              <div class="custom-template-text"
                   v-html="toast.text"></div>
            </div>
            <!--          <div class="custom-template-close"-->
            <!--               @click="props.close">-->
            <!--            <i class="icon ion-android-close"></i>-->
            <!--          </div>-->
          </div>
        </slot>
      </div>
    </transition-group>
  </div>
</template>
<script>
import velocity from 'velocity-animate';
import {Id} from './NotificationCenter';

const STATE = Object.freeze({
  IDLE: 0,
  ACTIVE: 1,
  CLOSED: 2,
  DELETED: 3
});
/**
 * notice: {
 *     // (optional)
 *     // Title (will be wrapped in div.notification-title)
 *     title: 'This is the <em>title</em>',
 *
 *     // Content (will be wrapped in div.notification-content)
 *     text: 'This is some <b>content</b>',
 *
 *     // (optional)
 *     // Class that will be assigned to the notification
 *     type: 'warn',
 *
 *     // (optional, override)
 *     // Time (in ms) to keep the notification on screen
 *     duration: 10000,
 *
 *     showUptil: () => {},
 *     showUntil: () => {},
 *
 *     closeOnClick: false,
 *
 *     // (optional)
 *     // Data object that can be used in your template
 *     data: {}
 *
 *     // (callbacks)
 *     // Called when it's shown on screen
 *     onStartedCallback: (notice) => {},
 *
 *     // Called when it's shown on screen
 *     onClosedCallback: (notice) => {},
 *
 *     // Called when it's destroyed
 *     onDeletedCallback: (notice) => {}
 *   }
 * */
/**
 * Sequential ID generator
 */

export default {
  data() {
    return {
      queue: new Queue(),
      activeList: [],
      velocity: velocity,
      selectedStyle: null,
    }
  },
  props: {
    // -- when using default template START -- //
    defaultTemplateClassName: {
      type: String, default: 'default-toast-top-layer'
    },
    // -- when using default template END -- //
    animation: {
      type: Object,
      default: () => ({
        /**
         * Animation function
         *
         * Runs before animating, so you can take the initial height, width, color, etc
         * @param  {HTMLElement}  el  The notification element
         * @param  complete
         */
        enter: (el, complete) => {
          this.velocity(el, {
            scale: [1, 0.2],
            opacity: [1, 0]
          }, {
            duration: 1000,
            complete
          });
        },
        leave: (el, complete) => {
          this.velocity(el, {
            scale: [0.2, 1],
            opacity: [0, 1]
          }, {
            duration: 1000,
            complete
          });
        }
      })
    },
    styles: {
      type: Function, default: (el) => {
      }, required: false
    },
    duration: {
      type: Number, default: 5000
    },
    max: {
      type: Number, default: Infinity
    },
    switchDelay: {
      type: Number, default: 100, required: false
    },
    destroyOnClose: {
      type: Boolean, default: true, required: false
    },
    pauseOnHover: {
      type: Boolean, default: true, required: false
    },
  },
  computed: {
    activeToasts() {
      return this.activeList.filter(toast => toast.state === STATE.ACTIVE);
    },
    botToTop() {
      return !!this.selectedStyle?.hasOwnProperty('bottom')
    },
  },
  methods: {
    // events
    onCloseCallback(toast) {
      toast.state = STATE.CLOSED;
      if (toast.onClosedCallback) toast.onClosedCallback(toast);
      // destroy after close
      if (this.destroyOnClose) this.onDestroyToastRequest(toast);

      // !important: try to move to next
      setTimeout(this.moveToNext, this.switchDelay);
    },
    onCloseToastRequest(toast) {
      if (!toast?.closeOnClick) return;
      if (toast.state !== STATE.ACTIVE) return;
      clearTimeout(toast.timerPID);
      toast.timerPID = null;
      toast.state = STATE.CLOSED;

      // destroy after close
      if (this.destroyOnClose) this.onDestroyToastRequest(toast);

      // !important: try to move to next
      setTimeout(this.moveToNext, this.switchDelay);
    },
    onDestroyToastRequest(toast) {
      toast.state = STATE.DELETED;
      this.cleanList();
      if (toast.onDestroyedCallback) toast.onDestroyedCallback(toast);
    },
    onToastEnter(el, complete) {
      this.selectedStyle = this.getStyle();
      this.animateToast('enter', el, complete);
    },
    onToastLeave(el, complete) {
      this.selectedStyle = this.getStyle();
      this.animateToast('leave', el, complete);
      setTimeout(this.moveToNext, this.switchDelay);
    },
    onToastAfterLeave() {
      this.cleanList();
    },
    onMouseEnterOverToast(toast) {
      if (toast.state === STATE.ACTIVE && this.pauseOnHover)
        toast.timer.pause();
    },
    onMouseLeaveOverToast(toast) {
      if (toast.state === STATE.ACTIVE && this.pauseOnHover)
        toast.timer.resume();
    },
    // actions
    cleanList() {
      this.activeList = this.activeList.filter(v => v.state === STATE.ACTIVE)
    },
    animateToast(direction, el, complete) {
      const animation = this.animation[direction];
      if (typeof animation === 'function')
        animation.call(this, el, complete);
    },
    getStyle() {
      return typeof this.styles === 'function'
          ? this.styles.call(this, this.$refs.toastRef)
          : this.styles;
    },
    timedToast(toast) {
      toast.data = toast.data || {};
      toast.type = toast.type || 'info';
      toast.type = 'toast-' + toast.type;
      toast.closeOnClick = toast?.closeOnClick || false;
      toast.duration = typeof toast.duration === 'number'
          ? toast.duration
          : this.duration;
      toast.showUntil = typeof toast.showUntil === 'function'
          ? toast.showUntil
          : undefined;
      toast.showWhenever = typeof toast.showWhenever === 'function'
          ? toast.showWhenever
          : undefined;
      toast.id = toast.id || Id();
      toast.onStartedCallback = typeof toast.onStartedCallback === 'function'
          ? toast.onStartedCallback
          : undefined;
      toast.onClosedCallback = typeof toast.onClosedCallback === 'function'
          ? toast.onClosedCallback
          : undefined;
      toast.onDestroyedCallback = typeof toast.onDestroyedCallback === 'function'
          ? toast.onDestroyedCallback
          : undefined;
      toast.show = true;

      let newToast = {...toast};
      if (toast.showWhenever) {
        newToast.timer = new Timer(() => this.onCloseCallback(newToast), 0, newToast);
      } else if (toast.showUntil) {
        newToast.timer = new Timer(() => this.onCloseCallback(newToast), toast.showUntil, newToast);
      } else {
        if (newToast.duration >= 0)
          newToast.timer = new Timer(() => this.onCloseCallback(newToast), newToast.duration, newToast);
      }
      if (toast.onStartedCallback) toast.onStartedCallback(newToast);
      toast.state = STATE.ACTIVE;
      return newToast;
    },
    moveToNext() {
      if (this.activeList.length < this.max && this.queue.length) {
        let timedToast = this.timedToast(this.queue.dequeue());
        if (this.botToTop) {
          this.activeList.push(timedToast)
        } else {
          this.activeList.unshift(timedToast)
        }
        timedToast.state = STATE.ACTIVE;
      }
    },
    addToast(toast) {
      toast.state = STATE.IDLE;
      this.queue.enqueue(toast);
      this.moveToNext();
    },
  },
  beforeDestroy() {
    this.activeToasts.forEach(toast => this.toast?.timer?.stop());
  }
}

class Timer {
  constructor(callback, delay, toast) {
    this.startedAt = Date.now();
    this.callback = callback;
    this.delay = delay;
    this.toast = toast;
    this.start();
    this.checkIntervalMs = 250;
  }

  start() {
    this.startedAt = Date.now();
    if (this.toast.showWhenever && typeof this.toast.showWhenever === 'function') {
      this.toast.timerPID = setInterval(() => {
        // if it returns true then keep timer on
        this.toast.show = this.toast.showWhenever();
      }, this.checkIntervalMs)
    } else if (typeof this.delay === 'function') {
      this.toast.timerPID = setInterval(() => {
        // if it returns true then keep timer on
        if (!this.delay()) this.stop();
      }, this.checkIntervalMs)
    } else {
      this.toast.timerPID = setTimeout(() => this.stop(), this.delay);
    }
  }

  pause() {
    clearTimeout(this.toast.timerPID);
    if (this.toast.showWhenever && typeof this.toast.showWhenever === 'function') {
      this.toast.show = true;
    } else if (typeof this.delay === 'function') {
      clearInterval(this.toast.timerPID)
    } else {
      this.delay -= Date.now() - this.startedAt;
    }
  }

  resume() {
    this.startedAt = Date.now();
    if (this.toast.showWhenever && typeof this.toast.showWhenever === 'function') {
      this.toast.timerPID = setInterval(() => {
        // if it returns true then keep timer on
        this.toast.show = this.toast.showWhenever();
      }, this.checkIntervalMs)
    } else if (typeof this.delay === 'function') {
      this.toast.timerPID = setInterval(() => {
        // if it returns true then keep timer on
        if (!this.delay()) this.stop();
      }, this.checkIntervalMs)
    } else {
      this.toast.timerPID = setTimeout(() => this.stop(), this.delay);
    }
  }

  stop() {
    if (typeof this.delay === 'function')
      clearInterval(this.toast.timerPID)
    else clearTimeout(this.toast.timerPID);
    this.callback(this.toast);
  }
}

export class Queue {
  constructor() {
    this.items = {};
    this.front = 0;
    this.rear = 0;
  }

  get queue() {
    let list = [];
    for (let i = this.front; i < this.rear; i++) list.push(this.items[i]);
    return list;
  }

  get length() {
    return this.rear - this.front;
  }

  enqueue(item) {
    this.items[this.rear] = item;
    this.rear++;
  }

  dequeue() {
    const item = this.items[this.front];
    delete this.items[this.front];
    this.front++;
    return item;
  }

  peek() {
    return this.items[this.front];
  }

  isEmpty() {
    return this.rear === 0;
  }
}
</script>
<style lang="scss">
.notification-group {
  display: block;
  position: absolute;
  pointer-events: none;

  .toast-top-layer {
    display: block;
    overflow: hidden;
    width: 100%;
    margin: 0;
    padding: 0;

    // --------- override toast msg style here START ---------- //
    .custom-template {
      display: flex;
      flex-direction: row;
      flex-wrap: nowrap;
      text-align: left;
      font-size: 13px;
      padding: 5px 5px 0 5px;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      pointer-events: auto;
      width: 100%;
      //width: max-content;
      &, & > div {
        box-sizing: border-box;
      }

      background: white;
      color: black;
      border: 2px solid black;
      border-radius: 18px;

      // define a specific msg type css here. (to display loading digital trainer, body error msg, camera access error)
      &.toast-loading {
        background: black;
        color: white;
        border-color: #D0F2E1;

        .custom-template-content {
          padding: 1px 8px;
          flex: 1 0 auto;

          .custom-template-title {
            letter-spacing: 1px;
            text-transform: none;
            font-size: 18px;
            font-weight: 600;
          }
        }
      }

      &.toast-info {
        background: white;
        color: black;
        border-color: black;
      }

      &.toast-warn {
        background: #FFB648C2;
        border-color: #f48a06;
      }

      &.toast-error {
        background: #e54d42a8;
        border-color: #B82E24;
      }

      &.toast-success {
        background: #E8F9F0;
        border-color: #1aec83;
      }

      .custom-template-icon {
        flex: 0 1 auto;
        color: #15C371;
        font-size: 32px;
        padding: 0 10px;
      }

      .custom-template-close {
        flex: 0 1 auto;
        padding: 0 20px;
        font-size: 16px;
        opacity: 0.2;
        cursor: pointer;

        &:hover {
          opacity: 0.8;
        }
      }

      .custom-template-content {
        padding: 2px;
        flex: 1 1 auto;

        .custom-template-title {
          letter-spacing: 1px;
          text-transform: none;
          font-weight: 600;
        }
      }
    }

    // --------- override toast msg style here END   ---------- //
  }
}
</style>